﻿import React, { useEffect, useState } from 'react';
import { problemDetailsOf } from '../../api-service';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { win } from '../../utilities';
import { CircularProgress } from '../circular-progress';
import { AbsolutelyCentered } from '../absolutely-centered';
import { useNotifyFailedRequest } from '../notifier/use-notify-failed-request';

const INLINE_VIEWABLE_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/html',
  'image/jpeg',
  'image/png',
  'image/gif',
]);

export const documentNotFoundMessage = 'Dokumentet findes ikke længere og kan ikke vises.';
export const loadingFailedMessage = 'Der skete en fejl ved indlæsning af dokumentet.';
export const documentNotFoundSystemCode = 'MUNICIPALITY_DOCUMENT_NOT_FOUND';

interface ViewerState {
  fileUrl: string;
  errorMessage: string;
  isLoading: boolean;
  canDisplayInline: boolean;
}

export function MunicipalityDocumentViewer(props: { documentId: string }) {
  const { documentId } = props;
  const client = moduleClientFactory.createAiSearchClient(false);
  const notifyFailedRequest = useNotifyFailedRequest();

  const [viewerState, setViewerState] = useState<ViewerState>({ isLoading: true, errorMessage: '', fileUrl: '', canDisplayInline: false });

  useEffect(() => {
    client
      .getMunicipalityAssistantDocumentFileByIdentifier({ documentId })
      .then(res => {
        const contentType = res.headers?.['content-type'] || res.data.type || 'application/octet-stream';
        const fileName = res.fileName || extractFileNameFromHeader(res.headers?.['content-disposition']) || 'dokument';
        const canDisplayInline = INLINE_VIEWABLE_TYPES.has(contentType.split(';')[0].trim());

        const file = new File([res.data], fileName, { type: contentType });
        const fileUrl = win.URL.createObjectURL(file);

        if (canDisplayInline) {
          document.body.style.overflow = 'hidden';
          document.body.style.margin = '0';
        } else {
          triggerDownload(fileUrl, fileName);
        }

        setViewerState(prevState => ({ ...prevState, fileUrl, canDisplayInline, isLoading: false }));
      })
      .catch(async reason => {
        const documentNotFound = (await problemDetailsOf(reason))?.systemCode === documentNotFoundSystemCode;
        if (!documentNotFound) {
          await notifyFailedRequest(reason);
        }
        setViewerState(prevState => ({ ...prevState, errorMessage: documentNotFound ? documentNotFoundMessage : loadingFailedMessage, isLoading: false }));
      });
  }, []);

  if (viewerState.isLoading) {
    return (
      <AbsolutelyCentered>
        <CircularProgress />
      </AbsolutelyCentered>
    );
  }
  if (viewerState.errorMessage) {
    return (
      <AbsolutelyCentered>
        <p>{viewerState.errorMessage}</p>
      </AbsolutelyCentered>
    );
  }
  if (viewerState.canDisplayInline) {
    return <iframe src={viewerState.fileUrl} title="dokument" width="100%" style={{ height: '100vh' }} />;
  }
  return (
    <AbsolutelyCentered>
      <p>Filen kan ikke vises i browseren og er blevet downloadet.</p>
    </AbsolutelyCentered>
  );
}

function extractFileNameFromHeader(header: string | undefined): string | undefined {
  if (!header) return undefined;
  const match = /filename\*=UTF-8''(.+?)(?:;|$)/i.exec(header);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function triggerDownload(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
